'use client';

import { useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';
import type { PaywallAction } from '@/shared/lib/api';
import { PaywallBullets } from '@/features/payment/ui/PaywallBullets';
import { PackPaywallContent } from './PackPaywallContent';

/**
 * Root-mounted dialog that renders whatever PaywallPayload the API put in
 * the current limit error. Everything (title, body, button labels) comes
 * from the server — no local copy. See apps/api/app/Dtos/PaywallPayload.php.
 *
 * `secondary_action.target` is a semantic route token (e.g. `project_angles`)
 * resolved to a concrete URL here. The button is hidden when the user is
 * already on the target screen so it doesn't duplicate Cancel.
 */
export function LimitPaywallDialog() {
  const router = useRouter();
  const pathname = usePathname();
  const payload = useLimitPaywallStore((s) => s.payload);
  const meta = useLimitPaywallStore((s) => s.meta);
  const hidePaywall = useLimitPaywallStore((s) => s.hidePaywall);
  const openPassPaywall = usePassPaywallStore((s) => s.open);
  // Canonical Facade Pack offer copy — shown when the user taps "Get Facade
  // Pack" on a bulletless alert (the render-queue limit), so the pack body
  // carries the real pack pitch, not the alert's parallel-renders context line.
  const packRequiredPayload = usePaywallPayload('PACK_REQUIRED');

  // True once the user accepts a bulletless pack offer (queue limit): swap this
  // dialog from the small alert to the full pack body in place.
  const [showPack, setShowPack] = useState(false);

  // While the Paddle checkout overlay is up we relax this dialog to non-modal.
  // A modal Radix Dialog locks `<body>` (`react-remove-scroll` →
  // `pointer-events: none` + focus trap); Paddle injects its overlay iframe at
  // `<body>` level, OUTSIDE this dialog's subtree, so under the lock it renders
  // but is unclickable. Relaxing the lock (rather than closing the dialog) keeps
  // the paywall mounted so it can show the post-purchase success in place
  // (parity with Apple/Android). Restored to modal on checkout.closed.
  const [checkoutActive, setCheckoutActive] = useState(false);

  const projectId = pathname?.match(/^\/project\/([^/]+)/)?.[1];
  // Pack offers bind to the building the limit fired on (error meta) and fall
  // back to the current route's project (preflight-routed payloads carry no
  // meta).
  const packProjectId = meta?.project_id ?? projectId;

  const close = () => {
    setShowPack(false);
    setCheckoutActive(false);
    hidePaywall();
  };

  const targetPath = (target: string | null): string | null => {
    if (!target) return null;
    switch (target) {
      case 'projects':
        return '/';
      // The standalone Views/angles library was merged into the Brief page —
      // route the server-driven angle-library target there.
      case 'project_angles':
        return projectId ? `/project/${projectId}/goals` : null;
      // References folded into the Brief page (Phase 2) — route the
      // server-driven reference target there.
      case 'project_references':
        return projectId ? `/project/${projectId}/goals` : null;
      default:
        return null;
    }
  };

  const handleAction = (action: PaywallAction) => {
    // A bulletless purchase_pack (the render-queue limit) renders as the small
    // alert; its CTA swaps THIS dialog to the full pack body in place rather
    // than closing — the alert gave the context the pack body lacks. Rich pack
    // offers (with bullets) render the pack body directly and never call this.
    if (action.kind === 'purchase_pack') {
      setShowPack(true);
      return;
    }
    close();
    switch (action.kind) {
      case 'purchase_tokens':
        router.push('/account/tokens');
        break;
      case 'open_pass_paywall':
        // Pass upsell for a Pass-liftable limit. Server `target` is the
        // entitlement key; the store highlight uses the short form. Post-pivot
        // the only purchasable Pass is `builder_pass` (BUILDER_PASS_REQUIRED);
        // pro/designer stay mapped for cached legacy payloads — the pass
        // store redirects those to the pack paywall.
        openPassPaywall(
          action.target === 'builder_pass'
            ? 'builder'
            : action.target === 'designer_pass'
              ? 'designer'
              : 'pro'
        );
        break;
      case 'delete_existing': {
        const path = targetPath(action.target);
        if (path) router.push(path);
        break;
      }
      case 'dismiss':
      default:
        // Unknown future action kinds degrade to dismiss — the catalog is
        // server-driven and a new kind may ship before this client maps it.
        break;
    }
  };

  // Hide the secondary action only when it would re-route to the screen the
  // user is already on (mostly `delete_existing` with a target). For
  // `purchase_tokens` we always show it — the destination is implicit and the
  // user came here specifically to act on it.
  const showSecondary = (() => {
    const action = payload?.secondary_action;
    if (!action) return false;
    if (action.kind === 'delete_existing') {
      const path = targetPath(action.target);
      if (!path) return false;
      return pathname !== path;
    }
    return true;
  })();

  // A RICH pack offer (purchase_pack carrying bullets — PACK_REQUIRED /
  // FREE_DESIGNS_EXHAUSTED) renders the pack body directly. A bulletless
  // purchase_pack (the render-queue limit) stays the small alert until the user
  // taps its CTA (`showPack`) — then it swaps to the canonical pack body so the
  // pitch isn't the alert's parallel-renders context line.
  const isRichPackOffer =
    payload?.primary_action?.kind === 'purchase_pack' &&
    (payload.bullets?.length ?? 0) > 0;
  // Builder Pass activation: a FREE_DESIGNS_EXHAUSTED payload whose primary
  // action is `activate_building` (not a purchase) — a Builder Pass holder on a
  // free-tier building with a slot free. It still renders in the pack body
  // (activate CTA + one-time kit alternative) and carries its own copy, so use
  // the payload verbatim — never the generic PACK_REQUIRED fallback.
  const isBuilderActivation =
    payload?.primary_action?.kind === 'activate_building';
  const showOwnBody = isRichPackOffer || isBuilderActivation;
  const showPackBody = !!payload && (showOwnBody || showPack);
  const packBodyPayload = showOwnBody
    ? payload
    : (packRequiredPayload ?? payload);

  return (
    <Dialog
      open={payload !== null}
      // Relaxed to non-modal only while the Paddle overlay is up, so its
      // body-level iframe stays clickable (see `checkoutActive`).
      modal={!checkoutActive}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      {/* The Facade Kit pack body dismisses via a top-right close (X) — its own
          footer "dismiss" button was removed (parity with Apple/Android, which
          close the pack sheet with a corner X). Every other payload keeps its
          in-footer dismiss/affirmative button and no corner X. */}
      <DialogContent showCloseButton={showPackBody} className="max-w-[460px]">
        {/* Facade Pack offers get the dedicated pack body: server copy +
            building chrome + reference price + direct-to-app CTA. Every other
            payload (including the info-only PACK_*_EXHAUSTED caps) keeps the
            generic limit layout. */}
        {showPackBody && packBodyPayload ? (
          <PackPaywallContent
            payload={packBodyPayload}
            projectId={packProjectId ?? undefined}
            onDismiss={close}
            onCheckoutOpen={() => setCheckoutActive(true)}
            onCheckoutClose={() => setCheckoutActive(false)}
          />
        ) : payload ? (
          <>
            <DialogHeader>
              <DialogTitle>{payload.title}</DialogTitle>
              {/* `max-w-[80%]` caps the body at 80% of the dialog content
                  width so the paragraph balances and the last line doesn't
                  strand a single orphan word. DialogDescription is already
                  left-aligned by default — mirrors Apple/Android.
                  `text-card-body` overrides the default `text-form-help` so
                  the subtitle matches the bullet text size (both 16px), in
                  parity with Apple/Android (both `cardBody`). */}
              <DialogDescription className="text-card-body max-w-[80%]">
                {payload.body}
              </DialogDescription>
            </DialogHeader>
            {/* Server-driven benefit bullets (e.g. INSUFFICIENT_TOKENS carries
                3) — rendered above the actions; absent on most payloads.
                `mt-6` gives the value-prop block a clear visual break under
                the title+subtitle header (which sit tight together) — calmer
                rhythm than uniform spacing through the body. */}
            <PaywallBullets bullets={payload.bullets} className="mt-6" />
            {payload.primary_action ? (
              <DialogFooter className="gap-2">
                <Button
                  variant="ghost"
                  onClick={() => handleAction(payload.dismiss_action)}
                >
                  {payload.dismiss_action.label}
                </Button>
                {showSecondary && payload.secondary_action && (
                  <Button
                    variant="outline"
                    onClick={() => handleAction(payload.secondary_action!)}
                  >
                    {payload.secondary_action.label}
                  </Button>
                )}
                <Button onClick={() => handleAction(payload.primary_action!)}>
                  {payload.primary_action.label}
                </Button>
              </DialogFooter>
            ) : (
              // Info-only paywall: the limit can't be relieved by an action
              // here (daily/monthly render caps, or already on the top Pass).
              // The single dismiss is styled as the affirmative, full-width
              // primary button — Apple-HIG single-action sheet. No CTA.
              <DialogFooter>
                <Button
                  className="w-full"
                  onClick={() => handleAction(payload.dismiss_action)}
                >
                  {payload.dismiss_action.label}
                </Button>
              </DialogFooter>
            )}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
